import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { ContentVariant, CreativeDetails, FieldValue, TemplateVariant } from '@/api/types';
import type { SessionId } from '@/etcher/types';

interface State {
    // Currently opened creative
    creative: CreativeDetails | null;
    // Currently selected variant index
    currentVariantIndex: number;
    // Etcher session ID
    etcherId: SessionId | null;
    // Font files
    fontFiles: Record<string, ArrayBuffer>;
    // Currently selected content variant
    selectedContentVariantIndex: number;
    // SVG files data
    svgFiles: Record<string, string>;
}

type Actions = {
    setCreative: (creative: CreativeDetails) => void;
    setCurrentVariantIndex: (index: number) => void;
    setSelectedContentVariantIndex: (index: number) => void;
    setEtcherId: (id: SessionId) => void;
    addContentVariant: (variant: ContentVariant) => void;
    removeContentVariant: (variantId: number) => void;
    setFieldValue: (variantId: number, fieldValue: FieldValue) => void;
    removeFieldValue: (variantId: number, fieldValueId: number) => void;
    addFontFile: (url: string, fontFile: ArrayBuffer) => void;
    addSvgFile: (url: string, content: string) => void;
    clear: () => void;
};

const initState: State = {
    creative: null,
    currentVariantIndex: -1,
    etcherId: null,
    fontFiles: {},
    selectedContentVariantIndex: -1,
    svgFiles: {},
};

export const useCreativeGeneratorStore = create<State & Actions>()(
    subscribeWithSelector(
        immer((set) => ({
            ...initState,

            setCreative: (creative) => set({ creative }),

            setCurrentVariantIndex: (index) => set({ currentVariantIndex: index }),

            setSelectedContentVariantIndex: (index) => set({ selectedContentVariantIndex: index }),

            setEtcherId: (id) => set({ etcherId: id }),

            addContentVariant: (variant) =>
                set((state) => {
                    if (state.creative) {
                        state.creative.contentVariants.push(variant);
                    }
                    return state;
                }),

            removeContentVariant: (variantId) =>
                set((state) => {
                    if (state.creative) {
                        state.creative.contentVariants = state.creative.contentVariants.filter(
                            (variant) => variant.id !== variantId,
                        );
                    }
                    return state;
                }),

            setFieldValue: (variantId, fieldValue) =>
                set((state) => {
                    if (state.creative) {
                        const variant = state.creative.contentVariants.find((v) => v.id === variantId);
                        if (variant) {
                            const existingIndex = variant.fieldValues.findIndex((fv) => fv.id === fieldValue.id);
                            if (existingIndex >= 0) {
                                const newFieldValue = Object.assign({}, variant.fieldValues[existingIndex], fieldValue);
                                newFieldValue.value = fieldValue.value;
                                variant.fieldValues[existingIndex] = newFieldValue;
                            } else {
                                variant.fieldValues.push(fieldValue);
                            }
                        }
                    }
                    return state;
                }),

            removeFieldValue: (variantId, fieldValueId) =>
                set((state) => {
                    if (state.creative) {
                        const variant = state.creative.contentVariants.find((v) => v.id === variantId);
                        if (variant) {
                            variant.fieldValues = variant.fieldValues.filter((fv) => fv.id !== fieldValueId);
                        }
                    }
                    return state;
                }),

            addFontFile: (url, fontFile) =>
                set((state) => {
                    state.fontFiles = { ...state.fontFiles, [url]: fontFile };
                    return state;
                }),

            addSvgFile: (url, svgFile) =>
                set((state) => {
                    state.svgFiles = { ...state.svgFiles, [url]: svgFile };
                    return state;
                }),

            clear: () => set(() => initState),
        })),
    ),
);
